import { useState, useEffect } from 'react';
import {
    DeviceType,
    DeviceOrientation,
    DeviceModel,
    DeviceInfo,
    DEFAULT_BREAKPOINTS,
} from '@/types/device.types';

/**
 * Custom hook for client-side device detection
 * Detects device type, orientation, and handles resize/orientation changes
 */
export function useDevice(initialDeviceInfo?: DeviceInfo): DeviceInfo {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
        if (initialDeviceInfo) {
            return initialDeviceInfo;
        }

        // Default device info for SSR
        return {
            type: DeviceType.DESKTOP,
            orientation: DeviceOrientation.UNKNOWN,
            model: DeviceModel.UNKNOWN,
            isTouch: false,
            isMobile: false,
            isTablet: false,
            isDesktop: true,
            isIpad: false,
            screenWidth: null,
            screenHeight: null,
        };
    });

    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') return;

        const detectClientDevice = (): DeviceInfo => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const userAgent = navigator.userAgent.toLowerCase();

            // Detect device model
            const model = detectDeviceModel(userAgent);

            // Detect device type based on width
            const type = detectDeviceTypeFromWidth(width, model);

            // Detect orientation
            const orientation =
                width > height
                    ? DeviceOrientation.LANDSCAPE
                    : DeviceOrientation.PORTRAIT;

            // Detect touch capability
            const isTouch =
                'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                /touch|mobile|tablet|ipad|iphone|android/i.test(userAgent);

            return {
                type,
                orientation,
                model,
                isTouch,
                isMobile: type === DeviceType.MOBILE,
                isTablet: type === DeviceType.TABLET,
                isDesktop: type === DeviceType.DESKTOP,
                isIpad: type === DeviceType.IPAD || model === DeviceModel.IPAD,
                screenWidth: width,
                screenHeight: height,
                userAgent,
            };
        };

        // Initial detection
        setDeviceInfo(detectClientDevice());

        // Handle resize and orientation change
        const handleResize = () => {
            setDeviceInfo(detectClientDevice());
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    return deviceInfo;
}

/**
 * Detects device model from user agent (client-side)
 */
function detectDeviceModel(ua: string): DeviceModel {
    // iPad detection (including iPad Pro with desktop user agent)
    if (
        /ipad/.test(ua) ||
        (/macintosh/.test(ua) && navigator.maxTouchPoints > 1)
    ) {
        return DeviceModel.IPAD;
    }

    // iPhone detection
    if (/iphone|ipod/.test(ua)) {
        return DeviceModel.IPHONE;
    }

    // Android tablet detection
    if (/android/.test(ua) && !/mobile/.test(ua)) {
        return DeviceModel.ANDROID_TABLET;
    }

    // Android phone detection
    if (/android.*mobile/.test(ua)) {
        return DeviceModel.ANDROID_PHONE;
    }

    // Windows tablet detection
    if (/windows.*touch/.test(ua) || /tablet pc/.test(ua)) {
        return DeviceModel.WINDOWS_TABLET;
    }

    // Desktop detection
    if (/windows|macintosh|linux/.test(ua) && !/mobile|tablet/.test(ua)) {
        return DeviceModel.DESKTOP;
    }

    return DeviceModel.UNKNOWN;
}

/**
 * Detects device type based on viewport width
 */
function detectDeviceTypeFromWidth(
    width: number,
    model: DeviceModel
): DeviceType {
    // iPad gets its own category
    if (model === DeviceModel.IPAD) {
        return DeviceType.IPAD;
    }

    // Mobile phones
    if (
        model === DeviceModel.IPHONE ||
        model === DeviceModel.ANDROID_PHONE ||
        width < DEFAULT_BREAKPOINTS.mobile
    ) {
        return DeviceType.MOBILE;
    }

    // Tablets
    if (
        model === DeviceModel.ANDROID_TABLET ||
        model === DeviceModel.WINDOWS_TABLET ||
        (width >= DEFAULT_BREAKPOINTS.mobile && width < DEFAULT_BREAKPOINTS.tablet)
    ) {
        return DeviceType.TABLET;
    }

    // Desktop
    if (width >= DEFAULT_BREAKPOINTS.tablet) {
        return DeviceType.DESKTOP;
    }

    return DeviceType.UNKNOWN;
}

/**
 * Hook to check if device is mobile
 */
export function useIsMobile(): boolean {
    const device = useDevice();
    return device.isMobile;
}

/**
 * Hook to check if device is tablet
 */
export function useIsTablet(): boolean {
    const device = useDevice();
    return device.isTablet;
}

/**
 * Hook to check if device is iPad
 */
export function useIsIpad(): boolean {
    const device = useDevice();
    return device.isIpad;
}

/**
 * Hook to check if device is desktop
 */
export function useIsDesktop(): boolean {
    const device = useDevice();
    return device.isDesktop;
}

/**
 * Hook to get current orientation
 */
export function useOrientation(): DeviceOrientation {
    const device = useDevice();
    return device.orientation;
}

/**
 * Hook to check if device is in portrait mode
 */
export function useIsPortrait(): boolean {
    const device = useDevice();
    return device.orientation === DeviceOrientation.PORTRAIT;
}

/**
 * Hook to check if device is in landscape mode
 */
export function useIsLandscape(): boolean {
    const device = useDevice();
    return device.orientation === DeviceOrientation.LANDSCAPE;
}
